import React from 'react';
import PropTypes from 'prop-types';
import InputContainer from 'components/InputContainer';
import SelectAttribute from 'components/SelectAttribute';
import CheckBoxAttribute from 'components/Fields/CheckBoxAttribute';
import TextAreaAttribute from 'components/Fields/TextAreaAttribute';
import ConsentChannelGroup from 'components/ConsentChannelGroup';
import { CONSENT_CHANNELS, LABEL_SUFFIX, CREDENTIAL_TYPES } from 'utils/constants';
import {
  ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
  ERROR_TEXT_EMPTY_REQUIRED_FIELD,
  ERROR_TEXT_NO_SELECTED_CHANNEL,
} from 'langs/en/errors';
import { generateHTMLIdFromKeyPath } from 'utils/idUtils';

class FieldConfigureGDPRConsent extends React.Component {

  validateAndChangeRequiredField = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (!value.trim()) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_EMPTY_REQUIRED_FIELD);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeRequiredIfCheckboxField = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (!value.trim()) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeLeadShareCheckbox = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    this.props.onFieldUIStateChange([...this.props.keyPath, 'gdprConsentData', 'leadShareOptIn', 'leadShareValue'], { required: value });

    const leadShareTextKeyPath = [...this.props.keyPath, 'gdprConsentData', 'leadShareOptIn', 'text'];
    const channelDataKeyPath = [...this.props.keyPath, 'gdprConsentData', 'leadShareOptIn', 'channelData'];
    const leadShareValueKeyPath = [...this.props.keyPath, 'gdprConsentData', 'leadShareOptIn', 'leadShareValue'];
    const { channelData } = this.props.values.editors.gdprConsentData.leadShareOptIn;
    const noChannelSelected = CONSENT_CHANNELS.filter((key) => channelData[key].include).length === 0;
    if (!value) {
      this.props.fieldValidityChanged(leadShareTextKeyPath, true);
      if (!this.props.values.editors.gdprConsentData.leadShareOptIn.leadShareValue.trim()) {
        this.props.fieldValidityChanged(leadShareValueKeyPath, true);
      }
      if (noChannelSelected) {
        this.props.fieldValidityChanged(channelDataKeyPath, true);
      } else {
        CONSENT_CHANNELS.forEach((channel) => {
          if (channelData[channel].include && !channelData[channel].text.trim()) {
            this.props.fieldValidityChanged([...channelDataKeyPath, channel, 'text'], true);
          }
        });
      }
    } else {
      if (!this.props.values.editors.gdprConsentData.leadShareOptIn.text.trim()) {
        this.props.fieldValidityChanged(leadShareTextKeyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
      }
      if (!this.props.values.editors.gdprConsentData.leadShareOptIn.leadShareValue.trim()) {
        this.props.fieldValidityChanged(leadShareValueKeyPath, false, ERROR_TEXT_EMPTY_REQUIRED_FIELD);
      }
      if (noChannelSelected) {
        this.props.fieldValidityChanged(channelDataKeyPath, false, ERROR_TEXT_NO_SELECTED_CHANNEL);
      } else {
        CONSENT_CHANNELS.forEach((channel) => {
          if (channelData[channel].include && !channelData[channel].text.trim()) {
            this.props.fieldValidityChanged([...channelDataKeyPath, channel, 'text'], false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
          }
        });
      }
    }
  }

  validateAndChangeDisclaimerCheckbox = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    const disclaimerTextKeyPath = [...this.props.keyPath, 'gdprConsentData', 'disclaimer', 'text'];
    if (!value) {
      this.props.fieldValidityChanged(disclaimerTextKeyPath, true);
    } else if (!this.props.values.editors.gdprConsentData.disclaimer.text.trim()) {
      this.props.fieldValidityChanged(disclaimerTextKeyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
    }
  }

  render() {
    const {
      values: { editors: { gdprConsentData } },
      uiState,
      keyPath,
      fieldValidityChanged,
      onFieldUIStateChange,
      onFieldValueChange,
      sfFieldData,
      availableCredentialTypes,
    } = this.props;
    const isShortCourseForm = availableCredentialTypes.map((obj) => obj.value).includes(CREDENTIAL_TYPES.SHORT_COURSE);
    const gdprConsentKeyPath = [...keyPath, 'gdprConsentData'];
    const disclaimer = gdprConsentData.disclaimer.include ? (
      <div className="mt-2 text-muted small" id={generateHTMLIdFromKeyPath([...gdprConsentKeyPath, 'disclaimer', 'helpText'])}>
        This text will appear at the bottom of this consent section.
      </div>
    ) : null;
    let pickListValueMap = {};

    if (sfFieldData.lead_share_opt_in) {
      pickListValueMap = sfFieldData.lead_share_opt_in.sfPicklistValues.reduce((acc, cur) => {
        acc[cur.value] = cur.label;
        return acc;
      }, {});
    }

    const LeadShareConsentContainer = (
      <div className="ml-3" id={generateHTMLIdFromKeyPath([...gdprConsentKeyPath, 'leadShareOptIn', 'leadShareConsentContainer'])}>
        <div className="row mb-2">
          <div className="col">
            <InputContainer
              label="Lead Share Value"
              keyPath={[...keyPath, 'gdprConsentData', 'leadShareOptIn', 'leadShareValue']}
              uiState={uiState.editors.gdprConsentData.leadShareOptIn.leadShareValue}
            >
              <SelectAttribute
                keyPath={[...keyPath, 'gdprConsentData', 'leadShareOptIn', 'leadShareValue']}
                value={gdprConsentData.leadShareOptIn.leadShareValue}
                options={pickListValueMap}
                onFieldValueChange={this.validateAndChangeRequiredField}
                onFieldUIStateChange={onFieldUIStateChange}
              />
            </InputContainer>
          </div>
        </div>
        <ConsentChannelGroup
          keyPath={[...gdprConsentKeyPath, 'leadShareOptIn', 'channelData']}
          formSubState={gdprConsentData.leadShareOptIn.channelData}
          uiSubState={uiState.editors.gdprConsentData.leadShareOptIn.channelData}
          onFieldValidityChange={fieldValidityChanged}
          onFieldValueChange={onFieldValueChange}
          onFieldUIStateChange={onFieldUIStateChange}
        />
        <div className="mt-2 ml text-muted small">All options will be presented as Yes / No radio buttons. Lead Share Opt-In value will inhered the selection in the US Marketing Consent Section.</div>
      </div>
    );

    return (
      <div className="row pr-5 pb-3">
        <div className="col">
          <div className="row mt-2 mb-4">
            <div className="col">
              <InputContainer
                label="Retail opt-in language * (required)"
                keyPath={[...gdprConsentKeyPath, 'retailOptIn', 'text']}
                uiState={uiState.editors.gdprConsentData.retailOptIn.text}
              >
                <TextAreaAttribute
                  keyPath={[...gdprConsentKeyPath, 'retailOptIn', 'text']}
                  value={gdprConsentData.retailOptIn.text}
                  onFieldValueChange={this.validateAndChangeRequiredField}
                  onFieldUIStateChange={onFieldUIStateChange}
                  placeholder="I would like to receive additional information about..."
                />
              </InputContainer>
              <div className="ml-3">
                <ConsentChannelGroup
                  keyPath={[...gdprConsentKeyPath, 'retailOptIn', 'channelData']}
                  formSubState={gdprConsentData.retailOptIn.channelData}
                  uiSubState={uiState.editors.gdprConsentData.retailOptIn.channelData}
                  onFieldValidityChange={fieldValidityChanged}
                  onFieldValueChange={onFieldValueChange}
                  onFieldUIStateChange={onFieldUIStateChange}
                />
                <div className="mt-2 text-muted small">All options will be presented as Yes / No radio buttons.</div>
              </div>
            </div>
          </div>
          <div className="row mb-4">
            <div className="col">
              <div className="d-flex w-100">
                <CheckBoxAttribute
                  className="mt-1"
                  keyPath={[...gdprConsentKeyPath, 'leadShareOptIn', 'include']}
                  onCheckBoxChange={this.validateAndChangeLeadShareCheckbox}
                  value={gdprConsentData.leadShareOptIn.include}
                  labelledBy={generateHTMLIdFromKeyPath([...gdprConsentKeyPath, 'leadShareOptIn', 'text']).concat(LABEL_SUFFIX)}
                  disabled={isShortCourseForm}
                />
                <div className="ml-1 w-100">
                  <div className="mb-2">
                    <InputContainer
                      label="Include lead share opt-in language (optional)"
                      keyPath={[...gdprConsentKeyPath, 'leadShareOptIn', 'text']}
                      uiState={uiState.editors.gdprConsentData.leadShareOptIn.text}
                      disabled={isShortCourseForm}
                    >
                      <TextAreaAttribute
                        keyPath={[...gdprConsentKeyPath, 'leadShareOptIn', 'text']}
                        value={gdprConsentData.leadShareOptIn.text}
                        onFieldValueChange={this.validateAndChangeRequiredIfCheckboxField}
                        onFieldUIStateChange={onFieldUIStateChange}
                        placeholder="Our technology partner works with multiple universities..."
                        include={gdprConsentData.leadShareOptIn.include}
                        disabled={isShortCourseForm}
                      />
                    </InputContainer>
                  </div>
                  {isShortCourseForm && <div className="mt-2 text-muted small">Short Course forms must use the gdprProspect2uOptIn field for lead share.</div>}
                  {gdprConsentData.leadShareOptIn.include ? LeadShareConsentContainer : null}
                </div>
              </div>
            </div>
          </div>
          <div className="row mb-4">
            <div className="col">
              <div className="d-flex w-100">
                <CheckBoxAttribute
                  className="mt-1"
                  keyPath={[...gdprConsentKeyPath, 'disclaimer', 'include']}
                  onCheckBoxChange={this.validateAndChangeDisclaimerCheckbox}
                  value={gdprConsentData.disclaimer.include}
                  labelledBy={generateHTMLIdFromKeyPath([...gdprConsentKeyPath, 'disclaimer', 'text']).concat(LABEL_SUFFIX)}
                />
                <div className="ml-1 w-100">
                  <InputContainer
                    label="Include disclaimer language (optional)"
                    keyPath={[...gdprConsentKeyPath, 'disclaimer', 'text']}
                    uiState={uiState.editors.gdprConsentData.disclaimer.text}
                  >
                    <TextAreaAttribute
                      keyPath={[...gdprConsentKeyPath, 'disclaimer', 'text']}
                      value={gdprConsentData.disclaimer.text}
                      onFieldValueChange={this.validateAndChangeRequiredIfCheckboxField}
                      onFieldUIStateChange={onFieldUIStateChange}
                      placeholder="By pressing submit, I consent to..."
                      include={gdprConsentData.disclaimer.include}
                    />
                  </InputContainer>
                  {disclaimer}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

FieldConfigureGDPRConsent.propTypes = {
  values: PropTypes.shape({
    editors: PropTypes.shape({
      gdprConsentData: PropTypes.shape({
        retailOptIn: PropTypes.shape({
          text: PropTypes.string,
          channelData: PropTypes.shape({
            email: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
            phone: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
            sms: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
          }),
        }),
        leadShareOptIn: PropTypes.shape({
          include: PropTypes.bool,
          text: PropTypes.string,
          leadShareValue: PropTypes.string,
          channelData: PropTypes.shape({
            email: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
            phone: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
            sms: PropTypes.shape({
              include: PropTypes.bool,
              text: PropTypes.string,
            }),
          }),
        }),
        disclaimer: PropTypes.shape({
          include: PropTypes.bool,
          text: PropTypes.string,
        }),
        default: PropTypes.bool,
      }),
    }).isRequired,
  }).isRequired,
  uiState: PropTypes.object.isRequired,
  keyPath: PropTypes.array.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  sfFieldData: PropTypes.objectOf(
    PropTypes.shape({
      sfPicklistValues: PropTypes.array,
    }).isRequired,
  ),
  availableCredentialTypes: PropTypes.array.isRequired,
};

FieldConfigureGDPRConsent.defaultProps = {
  sfFieldData: {
    sfPicklistValues: [],
  },
};

function makeFieldConfigGDPRConsent(props) {
  return (<FieldConfigureGDPRConsent {...props} />);
}

export default makeFieldConfigGDPRConsent;
